import { Component } from '@theme/component';
import { Scroller } from '@theme/scrolling';

/**
 * A horizontally scrolling, truly infinite row of review cards with
 * prev/next arrow controls — clicking "next" repeatedly keeps scrolling
 * forward forever (never jumps backward), and the same cards just keep
 * repeating; likewise "prev" keeps scrolling backward forever. The arrows
 * are never disabled.
 *
 * How it works: the card set is duplicated once (original + one aria-hidden
 * clone), and the carousel starts scrolled to the seam between the two
 * copies rather than to the very start — that gives one full set's width of
 * buffer in *both* directions before either arrow would run out of content.
 * Right as a step is about to run past either end, the scroll position is
 * silently reset — with no animation — by exactly one set's width. Since
 * the clone is pixel-identical to the original, that reset is invisible:
 * the shopper only ever sees smooth, continuous motion in the direction
 * they're clicking. The same check runs on every scroll event too (not
 * just arrow clicks), so dragging/swiping the list by hand is just as
 * infinite as the buttons.
 *
 * @typedef {Object} ReviewsCarouselRefs
 * @property {HTMLElement} list
 * @property {HTMLButtonElement} prevButton
 * @property {HTMLButtonElement} nextButton
 *
 * @extends {Component<ReviewsCarouselRefs>}
 */
class ReviewsCarousel extends Component {
  requiredRefs = ['list', 'prevButton', 'nextButton'];

  /** @type {Scroller | null} */
  #scroll = null;

  /** @type {number} */
  #setWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    const { list, prevButton, nextButton } = this.refs;

    this.#cloneCardSet();
    this.#setWidth = this.#measureSetWidth();

    // Start at the seam between the two copies, not at the very start —
    // that's what gives both arrows a full set's worth of buffer.
    if (this.#setWidth > 0) list.scrollLeft = this.#setWidth;

    this.#scroll = new Scroller(list, { onScroll: this.#wrapIfNeeded });
    prevButton.addEventListener('click', this.#handlePrev);
    nextButton.addEventListener('click', this.#handleNext);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroll?.destroy();
    this.refs.prevButton.removeEventListener('click', this.#handlePrev);
    this.refs.nextButton.removeEventListener('click', this.#handleNext);
  }

  /** Appends a second, aria-hidden copy of the current cards so there's always more to scroll into. */
  #cloneCardSet() {
    const { list } = this.refs;
    const originalCards = Array.from(list.querySelectorAll('.reviews-carousel__card'));
    if (originalCards.length === 0) return;

    for (const card of originalCards) {
      const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      list.append(clone);
    }
  }

  /** @returns {number} The pixel width of one full card set (half of the doubled-up list). */
  #measureSetWidth() {
    const { list } = this.refs;
    const cards = list.querySelectorAll('.reviews-carousel__card');
    const setSize = cards.length / 2;
    const firstOfSecondSet = cards[setSize];

    if (!(firstOfSecondSet instanceof HTMLElement)) return 0;

    return firstOfSecondSet.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
  }

  #handlePrev = () => {
    const { list } = this.refs;

    if (this.#setWidth > 0 && list.scrollLeft - this.#step() < 0) {
      // About to run past the start of the buffer — jump forward by one
      // set width first (instant, invisible), then continue backward.
      list.scrollLeft += this.#setWidth;
    }

    this.#scroll?.by(-this.#step());
  };

  #handleNext = () => {
    const { list } = this.refs;
    const maxScrollLeft = list.scrollWidth - list.clientWidth;

    if (this.#setWidth > 0 && list.scrollLeft + this.#step() > maxScrollLeft) {
      // About to run past the end of the buffer — jump back by one set
      // width first (instant, invisible), then continue forward.
      list.scrollLeft -= this.#setWidth;
    }

    this.#scroll?.by(this.#step());
  };

  /**
   * Runs on every scroll event (drag/swipe/trackpad, not just arrow
   * clicks): if the list has drifted within one card's width of either
   * true end, silently jump it back by one set width — same trick as the
   * button handlers, just triggered by scrolling itself instead of a
   * click, so free-scrolling never actually reaches the true edge either.
   */
  #wrapIfNeeded = () => {
    const { list } = this.refs;
    if (this.#setWidth <= 0) return;

    const maxScrollLeft = list.scrollWidth - list.clientWidth;
    const buffer = this.#step();

    if (list.scrollLeft <= buffer) {
      list.scrollLeft += this.#setWidth;
    } else if (list.scrollLeft >= maxScrollLeft - buffer) {
      list.scrollLeft -= this.#setWidth;
    }
  };

  /** @returns {number} The distance to scroll per arrow click: one card's width plus the gap. */
  #step() {
    const { list } = this.refs;
    const firstCard = list.querySelector('.reviews-carousel__card');
    if (!(firstCard instanceof HTMLElement)) return list.clientWidth;

    const gap = parseFloat(getComputedStyle(list).columnGap || '0') || 0;
    return firstCard.getBoundingClientRect().width + gap;
  }
}

if (!customElements.get('reviews-carousel-component')) {
  customElements.define('reviews-carousel-component', ReviewsCarousel);
}
